import type { StoryCtx } from './ctx.js';
import { childrenOf } from './tree.js';
import { mainTimelineOf, timeBasisOf, type TimeBasis } from './timeline.js';
import type { StoryNode, TimelineData } from './types.js';

/** Kürzeste Länge eines Zeitstrahls (Achseneinheiten) */
export const MIN_TIMELINE_LENGTH = 100;
/** Größte Lage auf der Fläche bzw. auf einem Zeitstrahl (wie bei allen Positionen) */
const MAX_COORD = 1_000_000;
/** Kleinere Abweichungen gelten als ausgerichtet (Achseneinheiten) */
const EPSILON = 1e-3;

const round6 = (v: number): number => Math.round(v * 1e6) / 1e6;

/** Lage und Streckung, mit der eine Zeitlinie unter derselben Zeit der Hauptzeitlinie liegt. */
interface Plan {
  /** Streckung um den Start der Zeitlinie (1 = keine) */
  k: number;
  /** neue Lage x der Zeitlinie auf der Fläche */
  x: number;
  /** neues Ende (der Start bleibt) */
  endPos: number;
}

function planOf(main: StoryNode, bm: TimeBasis, t: TimelineData, b: TimeBasis): Plan {
  const mt = main.timeline!;
  // gleicher Maßstab: so viel Zeit je Achseneinheit wie auf der Hauptzeitlinie; Positionen um den Start strecken, Zeiten bleiben
  const k = b.slope / bm.slope;
  const endPos = t.startPos + (t.endPos - t.startPos) * k;
  // der Start liegt senkrecht unter derselben Zeit der Hauptzeitlinie
  const startOnMain = mt.startPos + (b.at(t.startPos) - bm.at(mt.startPos)) / bm.slope;
  return { k, x: (main.x ?? 0) + startOnMain - t.startPos, endPos };
}

/**
 * Warum sich die Zeitlinie `tl` nicht an die Hauptzeitlinie koppeln lässt (mit ihren Daten `t`, falls sie gerade geändert werden);
 * null, wenn es geht. Nötig ist eine Zeitrechnung auf beiden mit derselben Vergleichsbasis (Datum bzw. eigene Zählung mit Nullpunkt,
 * oder beide eine eigene Zählung ohne Nullpunkt), und im Maßstab der Hauptzeitlinie muss sie auf die Fläche passen.
 */
export function couplingProblem(ctx: StoryCtx, tl: StoryNode, t: TimelineData | null = tl.timeline): string | null {
  const main = mainTimelineOf(ctx);
  if (tl.kind !== 'timeline' || !main || main.id === tl.id) return 'Die Hauptzeitlinie ist der Bezug: Koppeln lassen sich nur weitere Zeitlinien.';
  const bm = timeBasisOf(main.timeline);
  if (!bm) return 'Die Hauptzeitlinie hat keine Zeitrechnung: Gib ihr Start und Ende (Datum oder eigene Zählung).';
  const b = timeBasisOf(t);
  if (!b || !t) return 'Diese Zeitlinie hat keine Zeitrechnung: Gib ihr Start und Ende (Datum oder eigene Zählung).';
  if (b.basis !== bm.basis) {
    return bm.basis === 'ms'
      ? 'Diese Zeitlinie zählt für sich: Gib ihr einen Nullpunkt (Datum), dann lässt sie sich an die datierte Hauptzeitlinie koppeln.'
      : 'Die Hauptzeitlinie zählt für sich (ohne Datum): Gib ihr einen Nullpunkt oder dieser Zeitlinie eine eigene Zählung ohne Nullpunkt.';
  }
  const plan = planOf(main, bm, t, b);
  const length = plan.endPos - t.startPos;
  if (length < MIN_TIMELINE_LENGTH) {
    return `Im Maßstab der Hauptzeitlinie wäre diese Zeitlinie nur ${length < 1 ? length.toPrecision(2).replace('.', ',') : Math.round(length)} Einheiten lang (mindestens ${MIN_TIMELINE_LENGTH}). Strecke die Hauptzeitlinie oder verlängere diese Zeitspanne.`;
  }
  if (Math.abs(plan.endPos) > MAX_COORD || Math.abs(plan.x) > MAX_COORD || Math.abs(plan.x + plan.endPos) > MAX_COORD) {
    return `Im Maßstab der Hauptzeitlinie würde diese Zeitlinie zu lang (${Math.round(length).toLocaleString('de-DE')} Einheiten) oder läge zu weit draußen.`;
  }
  return null;
}

/** Was sich an einer Zeitlinie (und ihren Szenen) ändert, damit sie an der Hauptzeitlinie ausgerichtet ist. */
export interface Alignment {
  /** neue Lage x der Zeitlinie auf der Fläche */
  x: number;
  /** ihre Daten (Ende und Fähnchen gestreckt, Zeiten unverändert) */
  timeline: TimelineData;
  /** angedockte Szenen mit neuer Lage (nur die geänderten) */
  scenes: { id: string; at: number | null; from: number | null; to: number | null }[];
}

/**
 * Richtet die Zeitlinie `tl` an der Hauptzeitlinie aus („An Hauptzeitlinie koppeln“, PLAN.md §14): Maßstab angleichen – ihre Positionen
 * samt Fähnchen und angedockter Szenen werden um ihren Start gestreckt, sodass jede Zeit gleich bleibt – und `x` so setzen, dass ihr
 * Start senkrecht unter derselben Zeit der Hauptzeitlinie liegt. null, wenn sie schon ausgerichtet ist oder sich nicht koppeln lässt
 * (siehe `couplingProblem`).
 */
export function alignToMain(ctx: StoryCtx, tl: StoryNode): Alignment | null {
  const main = mainTimelineOf(ctx);
  const t = tl.timeline;
  if (!main || !t || couplingProblem(ctx, tl) !== null) return null;
  const plan = planOf(main, timeBasisOf(main.timeline)!, t, timeBasisOf(t)!);
  const stretch = Math.abs(plan.endPos - t.endPos) >= EPSILON;
  const move = Math.abs(plan.x - (tl.x ?? 0)) >= EPSILON;
  if (!stretch && !move) return null;
  const x = round6(plan.x);
  if (!stretch) return { x, timeline: t, scenes: [] };
  const at = (p: number): number => round6(t.startPos + (p - t.startPos) * plan.k);
  const timeline: TimelineData = { ...t, endPos: at(t.endPos), flags: t.flags.map((f) => ({ ...f, pos: at(f.pos) })) };
  const scenes = childrenOf(ctx, tl.id)
    .filter((n) => n.kind === 'event' || n.kind === 'period')
    .map((n) => (n.kind === 'event' ? { id: n.id, at: n.at === null ? null : at(n.at), from: null, to: null } : { id: n.id, at: null, from: n.from === null ? null : at(n.from), to: n.to === null ? null : at(n.to) }));
  return { x, timeline, scenes };
}

/**
 * Start oder Ende einer gekoppelten Zeitlinie geändert: Sie behält ihren Maßstab (den der Hauptzeitlinie) und wird länger oder
 * kürzer; Szenen und Fähnchen behalten Lage und Zeit. Liefert die Daten mit angepasstem `startPos`/`endPos`, null, wenn die neue
 * Zeitrechnung nicht zur bisherigen passt (andere Vergleichsbasis) – dann wird entkoppelt.
 */
export function keepScale(prev: TimelineData, next: TimelineData): TimelineData | null {
  const a = timeBasisOf(prev);
  const b = timeBasisOf(next);
  if (!a || !b || a.basis !== b.basis) return null;
  const posOf = (v: number): number => round6(prev.startPos + (v - a.at(prev.startPos)) / a.slope);
  return { ...next, startPos: posOf(b.at(next.startPos)), endPos: posOf(b.at(next.endPos)) };
}

/** Haben sich Start oder Ende (Datum bzw. eigene Zählung) zwischen zwei Ständen einer Zeitlinie geändert? */
export function timeEdgesChanged(prev: TimelineData, next: TimelineData): boolean {
  const r = prev.reckoning ?? null;
  const s = next.reckoning ?? null;
  if (!r !== !s) return true;
  if (r && s) return r.unit !== s.unit || r.start !== s.start || r.end !== s.end || (r.zero ?? null) !== (s.zero ?? null);
  return prev.startLabel !== next.startLabel || prev.endLabel !== next.endLabel;
}
